import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiMail,
  FiLock,
  FiCalendar,
  FiMapPin,
  FiBookOpen,
  FiBook,
  FiArrowLeft,
  FiSave,
  FiX
} from 'react-icons/fi';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PhoneInput from '../components/common/PhoneInput';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { studentsAPI, testAPI } from '../services/api';
import '../styles/students.css';

const NewStudent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    phoneNumber: '',
    address: '',
    state: '',
    examTypes: [],
    subjects: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAvailableSubjects();
  }, []);

  const fetchAvailableSubjects = async () => {
    try {
      const response = await testAPI.getSyllabusOptions();
      
      if (response.success) {
        // Get all unique subjects from all exam types
        const allSubjects = new Set();
        Object.values(response.data.subjects).forEach(examSubjects => {
          examSubjects.forEach(subject => allSubjects.add(subject));
        });
        setAvailableSubjects(Array.from(allSubjects));
      } else {
        console.error('Failed to load available subjects');
        // Fallback to empty array if API fails
        setAvailableSubjects([]);
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setAvailableSubjects([]);
    }
  };

  const examTypes = ['WAEC', 'JAMB', 'POST-UTME', 'BECE'];
  const subjects = availableSubjects;
  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
    'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
    'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));

    // Clear error when user makes selection
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm password';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.school.trim()) newErrors.school = 'School is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Array field validation
    if (formData.examType.length === 0) {
      newErrors.examType = 'Please select at least one exam type';
    }

    if (formData.subjects.length === 0) {
      newErrors.subjects = 'Please select at least one subject';
    }

    // Age validation (must be at least 10 years old)
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 10 || age > 25) {
        newErrors.dateOfBirth = 'Student must be between 10 and 25 years old';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Check user authentication and role
    console.log('🔍 Debug - User info:', { 
      user: user, 
      isAuthenticated: !!user, 
      role: user?.role,
      token: localStorage.getItem('token') || sessionStorage.getItem('token') ? 'present' : 'missing'
    });
    
    // Check if user is authenticated and has admin role
    if (!user) {
      showNotification('You must be logged in to create students', 'error');
      return;
    }
    
    if (user.role !== 'admin') {
      showNotification('Only administrators can create students', 'error');
      return;
    }
    
    if (!validateForm()) {
      showNotification('Please fix the errors in the form', 'error');
      return;
    }

    setLoading(true);
    try {
      const studentData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        dateOfBirth: formData.dateOfBirth,
        phoneNumber: formData.phoneNumber.trim(),
        state: formData.state,
        school: formData.school.trim(),
        examType: formData.examType,
        subjects: formData.subjects
      };

      console.log('🚀 Attempting to create student:', studentData);
      await studentsAPI.createStudent(studentData);
      showNotification('Student created successfully', 'success');
      navigate('/students');
    } catch (error) {
      console.error('❌ Error creating student:', error);
      console.error('❌ Error response:', error.response?.data);
      showNotification(error.response?.data?.message || 'Failed to create student', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/students');
  };

  return (
    <div className="students-page">
      <div className="students-header">
        <div className="header-content">
          <div className="header-left">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="back-button"
            >
              <FiArrowLeft />
              Back to Students
            </Button>
            <div className="header-title">
              <h1>
                <FiUser className="title-icon" />
                Add New Student
              </h1>
              <p>Create a new student account with all required information</p>
            </div>
          </div>
        </div>
      </div>

      <div className="students-content">
        <div className="new-student-form-container">
          <form onSubmit={handleSubmit} className="new-student-form">
            {/* Personal Information Section */}
            <div className="form-section">
              <h3 className="section-title">
                <FiUser />
                Personal Information
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <div className="input-with-icon">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="Enter email address"
                    />
                  </div>
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number (optional)</label>
                  <PhoneInput
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(v) => setFormData(prev => ({ ...prev, phoneNumber: v }))}
                    placeholder="e.g. 8012345678"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth *</label>
                  <div className="input-with-icon">
                    <FiCalendar className="input-icon" />
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className={errors.dateOfBirth ? 'error' : ''}
                    />
                  </div>
                  {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <div className="input-with-icon">
                    <FiMapPin className="input-icon" />
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={errors.state ? 'error' : ''}
                    >
                      <option value="">Select state</option>
                      {nigerianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  {errors.state && <span className="error-message">{errors.state}</span>}
                </div>
              </div>
            </div>

            {/* Account Security Section */}
            <div className="form-section">
              <h3 className="section-title">
                <FiLock />
                Account Security
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <div className="input-with-icon">
                    <FiLock className="input-icon" />
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={errors.password ? 'error' : ''}
                      placeholder="Enter password"
                    />
                  </div>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <div className="input-with-icon">
                    <FiLock className="input-icon" />
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={errors.confirmPassword ? 'error' : ''}
                      placeholder="Confirm password"
                    />
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>
            </div>

            {/* Academic Information Section */}
            <div className="form-section">
              <h3 className="section-title">
                <FiBook />
                Academic Information
              </h3>
              
              <div className="form-group">
                <label htmlFor="school">School *</label>
                <div className="input-with-icon">
                  <FiBookOpen className="input-icon" />
                  <input
                    type="text"
                    id="school"
                    name="school"
                    value={formData.school}
                    onChange={handleInputChange}
                    className={errors.school ? 'error' : ''}
                    placeholder="Enter school name"
                  />
                </div>
                {errors.school && <span className="error-message">{errors.school}</span>}
              </div>

              <div className="form-group">
                <label>Exam Types *</label>
                <div className="checkbox-group">
                  {examTypes.map(exam => (
                    <label key={exam} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={exam}
                        checked={formData.examType.includes(exam)}
                        onChange={(e) => handleCheckboxChange(e, 'examType')}
                      />
                      <span className="checkbox-text">{exam}</span>
                    </label>
                  ))}
                </div>
                {errors.examType && <span className="error-message">{errors.examType}</span>}
              </div>

              <div className="form-group">
                <label>Subjects *</label>
                <div className="checkbox-group">
                  {subjects.map(subject => (
                    <label key={subject} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={subject}
                        checked={formData.subjects.includes(subject)}
                        onChange={(e) => handleCheckboxChange(e, 'subjects')}
                      />
                      <span className="checkbox-text">{subject}</span>
                    </label>
                  ))}
                </div>
                {errors.subjects && <span className="error-message">{errors.subjects}</span>}
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                <FiX />
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Creating Student...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Create Student
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewStudent;
